'use client'

import { useEffect, useState } from 'react'
import { useParams } from 'next/navigation'
import NewsSkeleton from '@/components/NewsSkeleton'
import MastheadLayoutSkeleton from '@/components/skeletons/MastheadLayoutSkeleton'
import { getApiBaseUrl } from '@/lib/config'
import {
  MASTHEAD_LAYOUT,
  resolveMastheadLayout,
  type MastheadLayoutId,
} from '@/lib/category-masthead-layout'
import { peekCategoryMastheadLayout } from '@/lib/masthead-layout-cache'
import { SITE_CONTAINER, SITE_MAIN_PAD_Y, SITE_PAGE_BG } from '@/lib/site-layout'
import { cn } from '@/lib/utils'

const bar = 'animate-pulse rounded-md bg-gray-200 dark:bg-slate-700'

function PageHeaderSkeleton() {
  return (
    <div className="mb-4 sm:mb-6">
      <div className={cn(bar, 'h-8 w-48 max-w-[70%]')} />
    </div>
  )
}

/**
 * Kategori loading — session cache veya API’den gerçek manşet layout’unu kullanır.
 * `categorySlug`: navigasyon hedefi (gate); yoksa mevcut route params.
 */
export default function CategoryLoadingSkeleton({
  categorySlug,
}: {
  categorySlug?: string
} = {}) {
  const params = useParams()
  const slug = String(categorySlug || params?.category || '').trim()

  const cached = slug ? peekCategoryMastheadLayout(slug) : null
  const [layout, setLayout] = useState<MastheadLayoutId>(
    cached?.layout ?? MASTHEAD_LAYOUT.SLIDER_SIDEBAR,
  )
  const [showMasthead, setShowMasthead] = useState(cached?.showMasthead ?? true)

  useEffect(() => {
    if (!slug || slug === 'arama') return

    const peek = peekCategoryMastheadLayout(slug)
    if (peek) {
      setLayout(peek.layout)
      setShowMasthead(peek.showMasthead)
      return
    }

    let cancelled = false
    const ctrl = new AbortController()
    const timer = window.setTimeout(() => ctrl.abort(), 2500)

    void fetch(`${getApiBaseUrl()}/api/news/category/slug/${encodeURIComponent(slug)}`, {
      signal: ctrl.signal,
      cache: 'force-cache',
    })
      .then(async (res) => {
        if (!res.ok || cancelled) return
        const data = (await res.json()) as {
          categoryMastheadLayout?: number | null
          categoryMasthead?: number | boolean | null
        }
        const nextLayout = resolveMastheadLayout(data.categoryMastheadLayout, 'category')
        const mhOn = data.categoryMasthead === true || data.categoryMasthead === 1
        if (!cancelled) {
          setLayout(nextLayout)
          // Loading sırasında manşeti gizleme — skeleton→içerik flash artar
          if (mhOn) setShowMasthead(true)
        }
      })
      .catch(() => {
        /* varsayılan skeleton kalsın */
      })
      .finally(() => window.clearTimeout(timer))

    return () => {
      cancelled = true
      ctrl.abort()
      window.clearTimeout(timer)
    }
  }, [slug])

  return (
    <div className={SITE_PAGE_BG} aria-busy="true" aria-label="Sayfa yükleniyor">
      <main className={`${SITE_CONTAINER} ${SITE_MAIN_PAD_Y}`}>
        {/* Gerçek kategori: mb-4 + category-top (idle min 90px) — CLS için aynı boşluk */}
        <div className="mb-4" aria-hidden>
          <div className={cn(bar, 'h-[90px] w-full rounded-[5px]')} />
        </div>
        <PageHeaderSkeleton />
        {showMasthead ? <MastheadLayoutSkeleton layout={layout} /> : null}
        <NewsSkeleton count={8} columns={4} plain showDate={false} />
      </main>
    </div>
  )
}
